<script setup lang="ts">
import { useMagicKeys } from "@vueuse/core";

const {
  displaySide = "left",
  fullscreenFrom = "mobile",
  isOpen,
} = defineProps<{
  isOpen: boolean;
  fullscreenFrom?: "tablet" | "mobile";
  displaySide?: "left" | "right";
}>();

const emit = defineEmits<{ (e: "close"): void }>();

const closeSidebar = () => {
  emit("close");
};

const platformStore = usePlatformStore();
const { getStyleFromTheme } = useGetStyleFromTheme();
const platformStyles = computed(() =>
  getStyleFromTheme(platformStore.getPlatformConfiguration.styling),
);

watchEffect(() => {
  if (isOpen) {
    disableBodyScroll();
  } else {
    enableBodyScroll();
  }
});

// Close modal and press escape
const { escape } = useMagicKeys();
watchEffect(() => {
  if (escape?.value) closeSidebar();
});
</script>

<template>
  <Teleport to="#teleports">
    <div :class="`sheet-menu--fullscreen-from-${fullscreenFrom}`">
      <Transition name="slide">
        <!-- we already handle keyboard handling through a composable -->
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
        <div
          v-show="isOpen"
          class="sheet-menu"
          data-cy="sheet-menu"
          :data-visible="isOpen"
          data-test="sheet-menu"
          :style="platformStyles"
          @click.self="closeSidebar"
        >
          <section
            class="sheet-menu__container"
            :class="displaySide"
          >
            <slot />
          </section>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style lang="postcss" scoped>
.right {
  @apply right-0;
}

.left {
  @apply left-0;
}

.sheet-menu {
  @apply block fixed z-50 h-full bg-black/50 top-0;
  width: calc(100vw + 24rem);

  /* the extra 24rem is there to give room for the slide animation */
  &__container {
    @apply fixed w-full top-0 z-10 overflow-y-auto;
    @apply bg-platform-bg-1;

    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    height: 100dvh;
    @apply w-full md:w-96;
  }

  &--fullscreen-from-tablet .sheet-menu__container {
    @apply md:w-full lg:w-96;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  section {
    transition: all 0.3s ease;
  }
}

.slide-enter-from {
  @apply opacity-0;

  section {
    transition: all 0.3s ease;
    @apply opacity-0;
  }
}

.sheet-menu--fullscreen-from-mobile {
  .slide-enter-from {
    section {
      @apply translate-y-196 md:translate-y-0;
    }

    .left {
      @apply md:-translate-x-96;
    }
    .right {
      @apply md:translate-x-96;
    }
  }

  .slide-leave-to {
    section {
      @apply translate-y-196 md:translate-y-0;
    }

    .left {
      @apply md:-translate-x-96;
    }
    .right {
      @apply md:translate-x-96;
    }
  }
}

.sheet-menu--fullscreen-from-tablet {
  .slide-enter-from {
    section {
      @apply translate-y-196 lg:translate-y-0;
    }

    .left {
      @apply lg:-translate-x-96;
    }
    .right {
      @apply lg:translate-x-96;
    }
  }

  .slide-leave-to {
    section {
      @apply translate-y-196 lg:translate-y-0;
    }

    .left {
      @apply lg:-translate-x-96;
    }
    .right {
      @apply lg:translate-x-96;
    }
  }
}
</style>
